import { getThemeAsset } from "@utils/theme";
import { motion, useAnimationControls } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { CoinJumpAnimation } from "../CoinJumpAnimation";
import RegisterGiftText from "./RegisterGiftText";
import {
  ANIMATION_STATE,
  LOOP_CONFIG,
  finalGlowVariant,
  initialGlowVariant,
  sleep,
} from "./utils";

const ENTER_MS = 2000;

const GiftTextButton = () => {
  const controls = useAnimationControls();

  const [isCoinVisible, setIsCoinVisible] = useState(false);
  const [isGlowVisible, setIsGlowVisible] = useState(true);

  const coinTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const COIN_START_DELAY_MS = 1000;

  useEffect(() => {
    return () => {
      if (coinTimerRef.current) clearTimeout(coinTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      while (alive) {
        setIsGlowVisible(false);
        setIsCoinVisible(false);

        await controls.start(ANIMATION_STATE.IDLE);
        if (!alive) break;
        await sleep(LOOP_CONFIG.INITIAL_DELAY);
        if (!alive) break;
        setIsGlowVisible(true);
        if (coinTimerRef.current) clearTimeout(coinTimerRef.current);
        coinTimerRef.current = setTimeout(() => setIsCoinVisible(true), COIN_START_DELAY_MS);
        await controls.start(ANIMATION_STATE.ENTERING);
        if (!alive) break;
        await sleep(ENTER_MS);
        if (!alive) break;

        await controls.start(ANIMATION_STATE.PULSING_1);
        if (!alive) break;
        await sleep(ENTER_MS);
        await controls.start(ANIMATION_STATE.PULSING_2);
        if (!alive) break;
        await controls.start(ANIMATION_STATE.EXIT);
        if (!alive) break;
        await controls.start(ANIMATION_STATE.IDLE);
        if (!alive) break;
        setIsGlowVisible(false);
      }
    })();

    return () => {
      alive = false;
      controls.stop();
      if (coinTimerRef.current) clearTimeout(coinTimerRef.current);
    };
  }, [controls]);

  return (
    <div className="absolute inline-block top-0 left-1/2 -translate-x-1/2 h-full w-full -z-10">
      <motion.img
        src={getThemeAsset("/images/register-gift-label/bg-initial-glow.png")}
        variants={initialGlowVariant}
        initial={ANIMATION_STATE.IDLE}
        animate={controls}
        className="pointer-events-none absolute bottom-full left-1/2 mb-4 h-[88px] w-[410px] max-w-none"
        style={{
          zIndex: -2,
          bottom: "calc(100% + 16px - 40px)",
          clipPath: "inset(0 0 3px 0)",
          WebkitClipPath: "inset(0 0 3px 0)",
        }}
      />
      {isGlowVisible && (
        <motion.img
          key="ftd-gift-glow"
          src={getThemeAsset("/images/register-gift-label/bg-final-glow.png")}
          alt=""
          className="pointer-events-none absolute bottom-full left-1/2 mb-4 h-[88px] w-[410px] max-w-none"
          style={{
            zIndex: -2,
            bottom: "calc(100% + 16px - 40px)",
            clipPath: "inset(0 0 3px 0)",
            WebkitClipPath: "inset(0 0 3px 0)",
          }}
          variants={finalGlowVariant}
          initial={ANIMATION_STATE.IDLE}
          animate={controls}
          aria-hidden="true"
        />
      )}

      <CoinJumpAnimation isVisible={isCoinVisible} effectCount={4} />

      <div className="absolute bottom-full left-1/2 w-fit -translate-x-1/2 z-0 pointer-events-none">
        <RegisterGiftText zIndex={0} controls={controls} className="relative" />
      </div>
    </div>
  );
};

export default GiftTextButton;
